import { useRef, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FaTimes } from 'react-icons/fa';
import { Modal as MuiModal } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import tw from 'twin.macro';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import getScrollbarSize from '../../utils/getScrollbarSize';
import useWindowSize from '../../hooks/useWindowSize';
import useOnScrollStop from '../../hooks/useOnScrollStop';

const useStyles = makeStyles(() => ({
  modal: {
    '&': tw`overflow-y-auto text-center`,
  },
}));

const Modal = ({ children, open, onClose, className }) => {
  const classes = useStyles();
  const node = useRef();
  const windowSize = useWindowSize();
  const { onRefSet } = useOnScrollStop();
  const scrollbarWidth = useMemo(
    () => (typeof window !== 'undefined' ? getScrollbarSize().width : 0),
    [],
  );

  // TODO: move this to one place so can be used in other places (e.g. dropdown)
  useEffect(() => {
    const handleClick = (e) => {
      const { width } = getScrollbarSize();
      if (
        (node.current && node.current.contains(e.target)) ||
        windowSize.width - e.pageX <= width ||
        !!e.target.closest('.modal-safe-click-zone')
      ) {
        // inside click
        return;
      }
      // outside click
      onClose();
    };

    // add when mounted
    if (typeof window !== 'undefined' && open) {
      document.addEventListener('mousedown', handleClick);
    }

    // return function to be called when unmounted
    return () => {
      if (typeof window !== 'undefined') {
        document.removeEventListener('mousedown', handleClick);
      }
    };
  }, [onClose, open, windowSize.width]);

  return (
    <MuiModal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={clsx(classes.modal, className)}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        style: { marginRight: scrollbarWidth },
      }}
      ref={onRefSet}
    >
      <Fade in={open}>
        <>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" />
          &#8203;
          <div
            ref={node}
            className="container inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
          >
            {children}
            <div
              onClick={onClose}
              onKeyDown={onClose}
              tabIndex="-1"
              role="button"
              className="cursor-pointer z-50 absolute top-0 right-0 mr-2 mt-2"
            >
              <FaTimes />
            </div>
          </div>
        </>
      </Fade>
    </MuiModal>
  );
};

Modal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
};

Modal.defaultProps = {
  open: false,
  onClose: undefined,
  className: undefined,
};

export default Modal;
